import { Moment } from 'moment';
import { stringify } from 'query-string';
import type { Approval } from 'api/approval';
import {
  ExportPayload,
  ExportRequest,
  ExportResponse,
  FilterBy,
  ExportStatusType,
} from 'api/async-export';
import type { SimpleSearchFilter } from 'api/dashboard/widget';
import { FieldSectionType, FieldTypeDisplay } from 'api/field-sections/types';
import type { RatingFieldScale } from 'api/fields/fields';
import {
  UserStatus,
  Company,
  DateType,
  ProjectId,
  Team,
  User,
} from 'api/models';
import type { Color } from 'components/colors';
import type { Filter } from 'components/molecules/filters/types';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldFilter } from 'components/organisms/tasks/filters';
import type { GroupCriteria } from 'components/organisms/tasks/grouped-filter-popover';
import type { AccountFields } from 'data/fields/hooks/account-fields';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
import { fieldAccessMetaToBase3 } from 'utils/field-access';
import {
  ApiFilter,
  ApiGroupFilter,
} from 'utils/simple-search-filter/simple-search-filter';
import type { FileAttachment } from '../attachments';
import type { Project, ProjectPhase } from '../project';
import { WithNotStrategicTransformer } from './with-non-strategic-partner';

export { TaskApprovalAPI } from './approval';

// TODO: replace with generic status? ProjectPhaseStatus looks the same
// @abhilashlr @Shajansheriff
export enum TaskStatus {
  ToDo = 'Todo',
  InProgress = 'In progress',
  Completed = 'Completed',
  Blocked = 'Blocked',
  Proposed = 'Proposed',
  InPlanning = 'In Planning',
  ToBeStaffed = 'To be Staffed',
  Cancelled = 'Cancelled',
}

export enum TaskType {
  Task = 'TASK',
  Milestone = 'MILESTONE',
} // TODO:: This is just sample implementation, we can configure once we have use case.
export type FieldValueType = string | number | boolean | number[] | null;

interface AssigneeAudit {
  assignedAt: number;
  assignedBy: User;
  assignee: User | Team;
  assigneeType: 'USER' | 'TEAM';
}

export enum TaskSourceType {
  Document = 'DOCUMENT',
  DocumentSection = 'DOCUMENT_SECTION',
  SpaceTab = 'SPACE_TAB',
  Default = 'DEFAULT',
  Template = 'TEMPLATE',
}

export type ExternalResourceInputs = Record<string, string | string[]> & {
  issuetype: string;
  projectId: string;
  epicId: string;
};

export type LinkedResource = {
  resourceMappingId: number;
  integrationId: number;
  nativeResourceType: string;
  nativeResourceId: number;
  externalResourceType: string;
  externalResourceId: string;
  externalIntegrationType: string;
  externalResourceInputs: ExternalResourceInputs;
  enabled: boolean;
  deleted: boolean;
  value?: string;
  context?: {
    jiraProjectId?: string;
    jiraIssueUrl?: string;
  };
};

interface Source {
  sourceType: TaskSourceType;
  sourceId: number;
  metaData?: {
    [key: string]: string | number;
  };
}

export interface Task {
  taskId: number;
  taskName: string;
  taskDescription?: string;
  privateTaskDescription?: string;
  status: TaskStatus;
  assignee?: AssigneeValue;
  followers?: AssigneeValue; // followers and assignee are similar datatype
  dueDate?: DateType | null;
  startDate?: DateType | null;
  startDateActual?: DateType;
  dependentTasks: Array<DependentTask>;
  subTaskOrder?: number[];
  subTasks?: Array<Task>;
  project: Project;
  fields: Array<FieldValue<FieldValueType>>;
  attachments?: Array<FileAttachment>;
  effort?: number;
  atRisk?: boolean;
  // TODO: This should be projectTask?: number;
  parentTaskId?: number;
  parentTask?: number;
  parentTaskObject?: Task;
  private: boolean;
  approvals?: Array<Approval>;
  projectPhase?: ProjectPhase;
  completedAt?: number | null;
  assigneeAuditMeta: AssigneeAudit[];
  source?: Source;
  sources?: Source[];
  type: TaskType;
  progress: number;
  isCsatEnabled: boolean;
  responsible: Company[];
  csatRatings?: CsatRating[];
  createdBy: number;
  createdAt: number;
  linkedResources?: LinkedResource[];
  formInstanceId?: number;
  formDetails?: {
    headerImage?: string;
    responseCount?: number;
    formAnswerId?: number;
  };
  /** Timesheet fields */
  defaultTimeEntryBillable?: boolean;
  defaultTimeEntryCategory?: number;
  externalRefKey?: string;
  fieldsAccessMeta?: FieldAccessMeta;
  fieldsAccessMetaInBase3?: FieldAccessMetaInBase3;
}

export interface DuplicateTaskProps {
  duplicateCount: number;
  baseTaskId: number;
  itemsToDuplicate: string[];
  taskNames: string[];
  projectPhaseId: number;
}

export interface CsatRating {
  rating: number;
  ratedAt: number;
  ratedBy: User;
  comment?: string;
}

export type DependentTask = AtLeast<Task, 'taskId' | 'taskName' | 'project'>;

/* NOTE:: We are ITaskWithUid because we need a identifier to identify sub task.
when user create a new sub task, even before the create Promise has been resolved
we will push the record to array (state) and once the promise is resolved,
we will the query the temp sub task and push it with actual data.
TODO:: Find a better a way (equivalent to ember's store.createRecord()) to handle this
 */
export interface TaskWithUid extends Task {
  uid?: string;
}

export interface FieldValue<T> {
  fieldId: number;
  fieldName?: string;
  fieldValue: T;
  metaFieldValue?: FieldOption<T>;
  customField?: boolean;
  userCreated?: boolean;
  isEditable?: boolean;
}

export interface AssigneeValue {
  users: Array<User>;
  teams: Array<Team>;
}

export interface FieldOptionBase<T = number> {
  color?: Color;
  value: T;
  label: string;
  position?: number;
  active?: boolean;
  userStatus?: UserStatus;
}

export interface FieldOption<T> extends FieldOptionBase {
  color: Color;
  group: T;
}

export enum TaskStatusGroup {
  Todo = 1,
  InProgress,
  Completed,
  Blocked,
}

export enum StatusGroup {
  Todo = 1,
  InProgress,
  Completed,
  Blocked,
  Proposed = 5,
  InPlanning,
  ToBeStaffed,
  Cancelled,
}

export enum ProjectStatusGroup {
  Todo = 5,
  InProgress = 2,
  Completed = 3,
  'On hold' = 4,
  Cancelled = 8,
}

export type StatusFieldOption = Required<FieldOption<StatusGroup>>;
export type Decimals = number[];
export type FieldAccessMeta = Record<FieldSectionType, Decimals>;
export type Base3Number = number;
export type FieldAccessMetaInBase3 = Record<FieldSectionType, Base3Number[]>;

export interface Field {
  isEnabled: boolean;
  customField: boolean;
  fieldId: number;
  fieldName: string;
  fieldLabel?: string;
  fieldIndex?: number;
  description?: string;
  fieldType: string;
  fieldDataType: string;
  /** @deprecated */
  /* TO CHECK: need this to check default field or user-generated */
  fieldGroup?: string;
  private: boolean;
  optionsAvailable: boolean;
  fieldSectionId?: number;
  updatedBy?: number;
  updatedAt?: number;
  createdBy?: number;
  createdAt?: number;
  metaData?: {
    default?: FieldValueType;
    options?: Array<FieldOption<number | StatusGroup>>;
    ratingScale?: RatingFieldScale;
    relatedFieldId?: number;
    startKeyEventId?: number;
    endKeyEventId?: number;
    intervalBenchmark?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
  };
  objectType:
    | FieldSectionType.Project
    | FieldSectionType.Task
    | FieldSectionType.Users
    | FieldSectionType.Financial;
  fieldTypeDisplay?: FieldTypeDisplay;
  userGroupIds: number[];
  isEditable?: boolean;
}

export interface StatusField extends Field {
  metaData: {
    default: StatusGroup;
    options: StatusFieldOption[];
  };
}

export interface FieldWithValue extends Field {
  value?: FieldValueType;
}

export interface ProjectTaskFields {
  projectId?: number;
  fields?: Array<Field>;
}

// :TODO define type for all possible conditions
export interface TaskFilterCondition {
  fieldName: string;
  operator: string;
  fieldValue: any;
}

export interface UpdateSubTaskOrderPayload {
  subTaskOrder: number[];
}

export interface GroupExportProps {
  filters: GroupCriteria[];
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}
export interface ExportProps {
  filters: FieldFilter[];
  match: QueryCombinerType;
  columnOrder: string[];
  projectId?: number;
}
export type TaskUpdatePhasePayload = {
  projectPhase: Pick<ProjectPhase, 'projectPhaseId'>;
} & Pick<Task, 'startDate' | 'dueDate'>;

export type AddBulkTasksPayload = Array<{
  taskName: string;
}>;

export type BulkTaskPayload = {
  tasks: Array<{ taskId: number } & Partial<Task>>;
};
type BulkTaskOptions = {
  isAllTask?: boolean;
};

interface PaginatedTaskParams {
  startRow: number;
  endRow: number;
  filter: GroupCriteria[];
  filterType: QueryCombinerType;
  sort: string;
}
interface PaginatedTasklistParams extends PaginatedTaskParams {
  projectId: number;
  isEverything?: boolean;
}
interface PaginatedDashboardTaskParams {
  startRow: number;
  endRow: number;
  widgetFilter: SimpleSearchFilter;
  dashboardFilter: SimpleSearchFilter;
  sort: string;
  widgetId: number;
}

interface PaginatedTaskResponse {
  data: Task[];
  count: number;
}

class TasksHttpClient extends WithNotStrategicTransformer {
  public getTaskList = async (projectId: number): Promise<Task[]> =>
    this.getWithTransformer<Task[]>(`/projects/${projectId}/tasks`);

  public async getTask(projectID: number, taskID: number): Promise<Task> {
    return this.getWithTransformer<Task>(
      `/projects/${projectID}/tasks/${taskID}`,
    );
  }

  public async updateTask(
    projectID: number,
    taskID: number,
    data,
    isSync?: boolean,
  ): Promise<Task> {
    return this.putWithTransformer(
      `/projects/${projectID}/tasks/${taskID}${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
    );
  }

  public async bulkUpdateTask(data: BulkTaskPayload, options: BulkTaskOptions) {
    return this.put(`/bulk/tasks?isFromAllTasks=${options.isAllTask}`, data);
  }

  public async createTask(projectID: number, data): Promise<Task> {
    return this.postWithTransformer(`/projects/${projectID}/tasks/`, data);
  }

  public async createTaskWithForm({ projectId, data }): Promise<Task> {
    return this.postWithTransformer(`/projects/${projectId}/tasks/`, data);
  }

  public async duplicateTask(
    projectId: number,
    data: DuplicateTaskProps,
  ): Promise<void> {
    return this.post(`/projects/${projectId}/tasks/duplicate`, data);
  }

  public async addTaskTemplatesToProject(
    projectID: number,
    data,
  ): Promise<Task[]> {
    return this.postWithTransformer(
      `/projects/${projectID}/tasks/add-from-templates`,
      data,
    );
  }

  public createTaskWithRequestId = async (
    [, projectId]: ProjectId,
    data: Record<string, unknown>,
    requestId: string,
  ): Promise<Task> =>
    this.postWithTransformer(`/projects/${projectId}/tasks/`, data, {
      headers: { 'X-Request-Id': requestId },
    });

  public async getTaskFields(): Promise<AccountFields> {
    let data: AccountFields;

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.TaskFields,
      )) as AccountFields;
    } catch {
      data = await this.get<AccountFields>('/tasks/fields');
    }

    return data;
  }

  public async deleteTask(projectID: number, taskID: number): Promise<boolean> {
    return this.delete(`/projects/${projectID}/tasks/${taskID}/`);
  }

  public async createDependentTask(
    projectID: number,
    taskID: number,
    data,
    isSync?: boolean,
  ): Promise<Task> {
    return this.postWithTransformer(
      `/projects/${projectID}/tasks/${taskID}/add-dependency${
        isSync ? `?isSync=${true}` : ''
      }`,
      data,
    );
  }

  public async removeDependentTask(
    projectID: number,
    taskID: number,
    data,
  ): Promise<boolean> {
    return this.post(
      `/projects/${projectID}/tasks/${taskID}/remove-dependency`,
      data,
    );
  }

  public async deleteTaskAttachment(attachmentID: number) {
    return this.delete(`attachments/${attachmentID}/`);
  }

  public async addRating(
    taskID: number,
    data: { rating: number; comment?: string },
  ): Promise<boolean> {
    return this.post(`/tasks/${taskID}/add-rating`, data);
  }

  public async updatePrivacy(
    taskID: number,
    data: { private: boolean },
  ): Promise<Task> {
    return this.postWithTransformer(`/tasks/${taskID}/update-privacy`, data);
  }

  public getAllTasksLight = async () =>
    this.getWithTransformer<Task[]>('/tasks/light');

  public searchTasks = async (filter: Filter): Promise<Task[]> => {
    return this.getWithTransformer(
      `/tasks/search?${ApiFilter.toQueryStringFormat(filter.criteria).replace(
        /user-/g,
        '',
      )}&${stringify(
        {
          match: filter.criteria.length ? filter.type : null,
        },
        { skipNull: true },
      )}`,
    );
  };

  public getTasksAndMilestonesForProjects = async (
    projectId: number,
    isMilestone = false,
  ) => {
    return this.getWithTransformer(
      `/tasks/search?project.value=${projectId}&match=all${
        isMilestone ? '&type.value=MILESTONE' : ''
      }`,
    );
  };

  public getPaginatedTaskList = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
  }: PaginatedTaskParams): Promise<PaginatedTaskResponse> => {
    let url = '/tasks/searchlightV1?';
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);

    if (filter.length) {
      url += `${filterAsString}&match=${filterType}&`;
    }
    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedTaskResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public getPaginatedTaskListWithProject = async ({
    startRow,
    endRow,
    filter,
    sort,
    filterType,
    projectId,
    isEverything,
  }: PaginatedTasklistParams): Promise<PaginatedTaskResponse> => {
    let url = `/tasks/searchlightV1?projectId=${projectId}&`;
    const filterAsString = ApiGroupFilter.toQueryStringFormatForGroups(filter);
    const validFilterType = Object.values(QueryCombinerType).includes(
      filterType,
    )
      ? filterType
      : QueryCombinerType.All;

    if (isEverything) {
      url += 'everything=true&';
    }
    if (filter.length) {
      url += `${filterAsString}&match=${validFilterType}&`;
    }

    if (sort) {
      url += `sort=${sort}&`;
    }
    url += `offset=${startRow}&limit=${endRow - startRow}`;

    return this.get<PaginatedTaskResponse>(url).then((response) => ({
      ...response,
      data: response.data.map((task) =>
        task.fieldsAccessMeta != null
          ? {
              ...task,
              fieldsAccessMetaInBase3: fieldAccessMetaToBase3(
                task.fieldsAccessMeta,
              ),
            }
          : task,
      ),
    }));
  };

  public getSubTasks = (taskId: number): Promise<Task[]> => {
    return this.get(`/tasks/${taskId}/get-subtasks`);
  };

  public searchTasksForTimesheet = async (
    fromDateInMillisecond: Moment,
    toDateInMillisecond: Moment,
    projectId?: number,
  ): Promise<Task[]> => {
    return this.get(
      `/tasks/timesheet?${stringify({
        fromDate: fromDateInMillisecond.valueOf(),
        toDate: toDateInMillisecond.valueOf(),
        projectId,
      })}`,
    );
  };

  public getPaginatedTaskListForDashbaord = async ({
    dashboardFilter,
    widgetFilter,
    startRow,
    endRow,
    sort,
    widgetId,
  }: PaginatedDashboardTaskParams): Promise<PaginatedTaskResponse> => {
    let url = `/widgets/${widgetId}/drill-down-items?`;

    if (sort) {
      url += `sort=${sort}`;
    }
    url += `&offset=${startRow}&limit=${endRow - startRow}`;

    return this.post(url, {
      drillDownFilter: widgetFilter,
      dashboardFilter,
    });
  };

  public updateSubTaskOrder = (
    taskId: number,
    payload: UpdateSubTaskOrderPayload,
  ): Promise<void> => {
    return this.put(`/tasks/${taskId}/update-subtask-order`, payload);
  };

  public updatePhase = (
    taskId: number,
    payload: TaskUpdatePhasePayload,
  ): Promise<Task> => {
    return this.putWithTransformer(`/tasks/${taskId}/update-phase`, payload);
  };

  public exportAsCSVAsyncForGroupType = ({
    filters,
    match,
    columnOrder,
    projectId,
  }: GroupExportProps): Promise<ExportResponse> => {
    const filterString = ApiGroupFilter.toQueryStringFormatForGroups(filters);
    const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    let filterBy: FilterBy = {
      match,
      ...filterObject,
    };

    if (projectId) {
      filterBy = { ...filterBy, projectId };
    }
    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public exportAsCSVAsyncForNonGroup = ({
    filters,
    match,
    columnOrder,
    projectId,
  }: ExportProps): Promise<ExportResponse> => {
    const filterString = ApiFilter.toQueryStringFormat(filters);
    const filterObject = ApiFilter.convertQueryStringToObject(filterString);

    let filterBy: FilterBy = {
      match,
      ...filterObject,
    };

    if (projectId) {
      filterBy = { ...filterBy, projectId };
    }
    const request: ExportRequest = {
      filterBy,
      columnOrder,
    };

    const data: ExportPayload = {
      statusType: ExportStatusType.TaskExport,
      statusMeta: { request },
    };

    return this.post<ExportResponse, ExportPayload>('async/status', data);
  };

  public async addMultipleSubtasks(
    parentTaskId: number,
    data: AddBulkTasksPayload,
  ) {
    return this.post(`tasks/${parentTaskId}/create-bulk-subtasks`, data);
  }

  public async addMultipleTasks(
    parentTaskId: number,
    data: AddBulkTasksPayload,
  ) {
    return this.post(`projects/${parentTaskId}/tasks/bulk-create`, data);
  }

  public async logFilters(filter: Filter) {
    let url = '/tasks/searchlightV1?';
    const filterAsString = ApiFilter.toQueryStringFormat(filter.criteria);

    if (filter.criteria.length) {
      url += `${filterAsString}&match=${filter.type}&`;
    }
    this.get(url);
  }

  public async removeAsSubtask(taskId: number) {
    return this.put(`/tasks/remove-as-subtask/${taskId}`);
  }
}

export const TaskAPI = new TasksHttpClient();
